<script lang="ts">
  import type { Snippet } from 'svelte';
  import ClinicBrandMark from '../components/ClinicBrandMark.svelte';
  import type { BrandMark } from '../lib/branding.svelte';
  import type { NavSection, Tab } from '../lib/navigation';
  import { labelForTab } from '../lib/navigation';
  import { planLabel } from '../lib/tenant.svelte';
  import { TOSCANA_NAME, loadLocalBranding } from '../lib/branding.svelte';

  let {
    sections, activeTab, tenantName, brandMark, planSlug = null,
    roleLabel = null, usuario = '', trialDays = null, trialStatus = null,
    onNavigate, onLogout, onStartTour, children,
  }: {
    sections: NavSection[]; activeTab: Tab; tenantName: string; tenantId: string;
    brandMark: BrandMark; orgTipo?: string | null; planSlug?: string | null;
    roleLabel?: string | null; usuario?: string;
    trialDays?: number | null; trialStatus?: string | null;
    onNavigate: (tab: Tab) => void; onLogout: () => void; onStartTour?: () => void;
    children: Snippet;
  } = $props();

  let mobileOpen = $state(false);
  const pageTitle = $derived(labelForTab(activeTab, sections));
  const plan = $derived(planLabel(planSlug));

  // Encontrar la sección activa para mostrar en breadcrumb
  const activeSection = $derived(sections.find(s => s.items.some(i => i.id === activeTab)));
  let local = $state<ReturnType<typeof loadLocalBranding>>(loadLocalBranding());

  function refreshBranding() {
    local = loadLocalBranding();
  }

  $effect(() => {
    refreshBranding();
    window.addEventListener('toscana-branding-updated', refreshBranding);
    return () => window.removeEventListener('toscana-branding-updated', refreshBranding);
  });
</script>

<div class="drawer lg:drawer-open">
  <input id="main-drawer" type="checkbox" class="drawer-toggle" bind:checked={mobileOpen} />
  <div class="drawer-content flex flex-col min-h-screen">
    <!-- ===== NAVBAR MEJORADO ===== -->
    <div class="navbar">
      <div class="navbar-left">
        <div class="nav-info">
          {#if activeSection}
            <span class="nav-section">{activeSection.label}</span>
          {/if}
          <h1 class="nav-title">{pageTitle}</h1>
        </div>
      </div>

      <div class="navbar-right">
        <div class="nav-actions">
          {#if onStartTour}
            <button class="nav-btn" onclick={onStartTour} title="Recorrido guiado">🎓</button>
          {/if}
        </div>

        <div class="nav-user">
          {#if trialStatus === 'trial' && trialDays != null}
            <span class="trial-chip" class:trial-warn={trialDays <= 7} class:trial-critical={trialDays <= 3}>
              {trialDays} día{ trialDays !== 1 ? 's' : '' } de prueba
            </span>
          {:else if trialStatus === 'suspended'}
            <span class="trial-chip trial-critical">Cuenta suspendida</span>
          {/if}
          <div class="user-avatar">
            {#if local.profileImage}
              <img src={local.profileImage} alt="" class="avatar-img" />
            {:else}
              {usuario ? usuario.charAt(0).toUpperCase() : '👤'}
            {/if}
          </div>
          <div class="user-info">
            <span class="user-name">{usuario}</span>
            {#if roleLabel}<span class="user-role">{roleLabel}</span>{/if}
          </div>
          <button class="logout-btn" onclick={onLogout} title="Cerrar sesión">⏻</button>
        </div>
      </div>
    </div>

    <main class="main-content">
      {@render children()}
    </main>
  </div>

  <!-- ===== SIDEBAR ===== -->
  <div class="drawer-side z-40">
    <label for="main-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
    <div class="sidebar">
      <div class="sidebar-brand">
        <ClinicBrandMark mark={brandMark} size="lg" />
        <div class="sidebar-brand-text">
          <strong>{tenantName || TOSCANA_NAME}</strong>
          {#if plan}<span class="sidebar-plan">{plan}</span>{/if}
        </div>
      </div>
      <ul class="menu menu-sm flex-1 p-2 gap-0.5">
        {#key sections.length}
        {#each sections as sec (sec.id)}
          <li class="menu-title">{sec.label}</li>
          {#each sec.items as item (item.id)}
            <li>
              <button class:active={activeTab === item.id} onclick={() => { onNavigate(item.id); mobileOpen = false; }}>
                {item.label}
              </button>
            </li>
          {/each}
        {/each}
        {/key}
      </ul>
      <div class="sidebar-footer">
        {#if local.horario}
          <div class="sf-item">🕐 {local.horario}</div>
        {/if}
        {#if local.telefono}
          <div class="sf-item">📞 {local.telefono}</div>
        {/if}
        <div class="sf-brand">Toscana Systems</div>
      </div>
    </div>
  </div>
</div>

<style>
  /* ===== NAVBAR ===== */
  .navbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.55rem 1.25rem;
    min-height: 52px;
    background: var(--navbar-bg, rgba(255,255,255,0.75));
    backdrop-filter: blur(20px) saturate(150%);
    -webkit-backdrop-filter: blur(20px) saturate(150%);
    border-bottom: 1px solid color-mix(in srgb, var(--brand,#0d9488) 12%, transparent);
    box-shadow: 0 2px 16px rgba(0,0,0,0.04);
    color: var(--navbar-text, #0f766e);
  }

  .navbar-left {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    flex: 1;
    min-width: 0;
  }

  .nav-info {
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
    min-width: 0;
  }
  .nav-section {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: color-mix(in srgb, var(--navbar-text, #0f766e) 55%, transparent);
  }
  .nav-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--navbar-text, #0f766e);
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .navbar-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
    margin-left: 1rem;
  }

  .nav-actions {
    display: flex;
    gap: 0.3rem;
  }
  .nav-btn {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid color-mix(in srgb, var(--brand,#0d9488) 15%, transparent);
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    font-size: 1rem;
    transition: all .15s;
  }
  .nav-btn:hover { background: color-mix(in srgb, var(--brand,#0d9488) 8%, transparent); }

  .nav-user {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.3rem 0.6rem;
    background: rgba(0,0,0,0.03);
    border-radius: 10px;
  }
  .user-avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 0.85rem;
    flex-shrink: 0;
    overflow: hidden;
  }
  .avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .user-info {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
  }
  .user-name {
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--navbar-text, #0f766e);
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .user-role {
    font-size: 0.6rem;
    color: color-mix(in srgb, var(--navbar-text, #0f766e) 55%, transparent);
    font-weight: 600;
  }
  .logout-btn {
    border: 0;
    background: transparent;
    font-size: 1.1rem;
    cursor: pointer;
    padding: 0.25rem;
    color: color-mix(in srgb, var(--navbar-text, #0f766e) 50%, transparent);
    transition: color .15s;
  }
  .logout-btn:hover { color: #dc2626; }

  .trial-chip {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 12px;
    background: #ecfdf5;
    color: #0f766e;
    border: 1px solid rgba(15,118,110,0.15);
    white-space: nowrap;
  }
  .trial-chip.trial-warn { background: #fefce8; color: #a16207; border-color: rgba(161,98,7,0.2); }
  .trial-chip.trial-critical { background: #fef2f2; color: #dc2626; border-color: rgba(220,38,38,0.2); }

  /* ===== MAIN CONTENT ===== */
  .main-content {
    flex: 1;
    padding: 1rem 1.25rem 1.5rem;
    max-width: 1280px;
    width: 100%;
    margin: 0 auto;
    background: transparent;
  }

  /* ===== SIDEBAR ===== */
  .sidebar {
    width: 250px;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--sidebar-bg, #0c3d3a);
    color: var(--sidebar-text, #fff);
  }

  .sidebar-brand {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 1rem 1rem 0.85rem;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .sidebar-brand-text {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
  }
  .sidebar-brand-text strong {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--sidebar-text, #fff);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sidebar-plan {
    font-size: 0.58rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background: color-mix(in srgb, var(--brand,#0d9488) 35%, transparent);
    border: 1px solid rgba(255,255,255,0.12);
    color: var(--sidebar-text, #fff);
    width: fit-content;
  }

  .menu-title {
    font-size: 0.6rem !important;
    font-weight: 700 !important;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 0.45rem 0.65rem 0.15rem !important;
    color: color-mix(in srgb, var(--sidebar-text, #fff) 45%, transparent) !important;
    opacity: 1 !important;
  }

  .sidebar-footer {
    padding: 0.75rem 1rem;
    border-top: 1px solid rgba(255,255,255,0.06);
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.62rem;
    color: color-mix(in srgb, var(--sidebar-text, #fff) 35%, transparent);
  }
  .sf-item {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sf-brand {
    font-size: 0.58rem;
    margin-top: 0.3rem;
    opacity: 0.4;
  }

  /* ===== MOBILE ===== */
  @media (max-width: 1023px) {
    .user-info { display: none; }
    .nav-section { display: none; }
    .nav-title { margin-left: 0.5rem; }
  }
</style>
